import React, { useRef } from "react";

import { useDispatch } from "react-redux";

import { FILE_EXTENSIONS } from "@/constants/file";
import { setMessage } from "@/redux/notification";

import { Button } from "@components/ui/button";

import {
  formatFileSize,
  isValidFileExtension,
  isValidFileSize,
} from "@utils/fileFormat";

type UploadButtonProps = {
  onFilesSelected: (files: File[]) => void;
  accept?: readonly string[];
  multiple?: boolean;
  maxSize?: number;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export const UploadButton = ({
  onFilesSelected,
  accept = FILE_EXTENSIONS.DOCUMENTS,
  multiple = false,
  maxSize,
  disabled = false,
  isLoading = false,
  className,
  children = "Upload",
}: UploadButtonProps) => {
  const dispatch = useDispatch();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files) {
      return;
    }

    const fileArray = Array.from(files);

    // Normalize extensions to include dots for validation
    const acceptWithDots = accept.map((ext) =>
      ext.startsWith(".") ? ext : `.${ext}`
    );

    // Validate file types
    const invalidFiles = fileArray.filter(
      (file) => !isValidFileExtension(file, acceptWithDots)
    );

    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.map((f) => f.name).join(", ");
      const acceptedTypesDisplay = accept
        .map((ext) => ext.replace(/^\./, ""))
        .join(", ");
      const message =
        invalidFiles.length > 1
          ? `The selected files (${fileNames}) have invalid types. Accepted types: ${acceptedTypesDisplay}`
          : `"${fileNames}" has an invalid file type. Accepted types: ${acceptedTypesDisplay}`;

      dispatch(
        setMessage({
          content: message,
          type: "error",
        })
      );
      return;
    }

    // Validate file sizes
    if (maxSize) {
      const oversizedFiles = fileArray.filter(
        (file) => !isValidFileSize(file, maxSize)
      );
      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map((f) => f.name).join(", ");
        const formattedSize = formatFileSize(maxSize);
        const message =
          oversizedFiles.length > 1
            ? `The selected files (${fileNames}) exceed the size limit. Maximum size: ${formattedSize}`
            : `"${fileNames}" exceeds the size limit. Maximum size: ${formattedSize}`;

        dispatch(
          setMessage({
            content: message,
            type: "error",
          })
        );
        return;
      }
    }

    onFilesSelected(fileArray);

    // Reset the input value so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept
          .map((ext) => (ext.startsWith(".") ? ext : `.${ext}`))
          .join(",")}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <Button
        onClick={handleButtonClick}
        disabled={disabled}
        isLoading={isLoading}
        size="sm"
        className={className}
      >
        {children}
      </Button>
    </>
  );
};

export default UploadButton;
